import React, { useCallback, useEffect, useState } from 'react';
import { Plus, X, Loader2, FileDown, ArrowRight, UserCheck } from 'lucide-react';
import { apiClient, API } from '../../services/api';
import { toast } from '../../hooks/use-toast';

const api = {
  jobs: () => apiClient.get('/hr/recruitment/jobs').then((r) => r.data),
  createJob: (p) => apiClient.post('/hr/recruitment/jobs', p).then((r) => r.data),
  patchJob: (id, p) => apiClient.patch(`/hr/recruitment/jobs/${id}`, p).then((r) => r.data),
  deleteJob: (id) => apiClient.delete(`/hr/recruitment/jobs/${id}`).then((r) => r.data),
  candidates: (jobId, sort) => apiClient.get(`/hr/recruitment/candidates?${jobId ? 'req_id=' + jobId + '&' : ''}${sort ? 'sort=' + sort : ''}`).then((r) => r.data),
  createCandidate: (p) => apiClient.post('/hr/recruitment/candidates', p).then((r) => r.data),
  moveStage: (id, p) => apiClient.patch(`/hr/recruitment/candidates/${id}/stage`, p).then((r) => r.data),
  deleteCandidate: (id) => apiClient.delete(`/hr/recruitment/candidates/${id}`).then((r) => r.data),
  rescore: (id) => apiClient.post(`/hr/recruitment/candidates/${id}/rescore`).then((r) => r.data),
  rescoreAll: (req_id) => apiClient.post(`/hr/recruitment/rescore-all${req_id ? '?req_id=' + req_id : ''}`).then((r) => r.data),
};

const STAGES = [
  { v: 'applied', l: 'Applied', color: 'bg-slate-200 text-slate-800' },
  { v: 'screening', l: 'Screening', color: 'bg-blue-100 text-blue-800' },
  { v: 'interview', l: 'Interview', color: 'bg-purple-100 text-purple-800' },
  { v: 'offered', l: 'Offered', color: 'bg-amber-100 text-amber-800' },
  { v: 'hired', l: 'Hired', color: 'bg-emerald-100 text-emerald-800' },
  { v: 'rejected', l: 'Rejected', color: 'bg-red-100 text-red-800' },
];
const stageMeta = (v) => STAGES.find((s) => s.v === v) || STAGES[0];

const downloadLetter = async (candidateId, kind, name) => {
  try {
    const token = localStorage.getItem('emerald_token') || '';
    const res = await fetch(`${API}/hr/recruitment/candidates/${candidateId}/${kind}-letter.pdf`, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) throw new Error((await res.json()).detail || 'Failed');
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `${kind}_${name.replace(/\s+/g, '_')}.pdf`; a.click();
    URL.revokeObjectURL(url);
  } catch (e) { toast({ title: e.message || 'Failed', variant: 'destructive' }); }
};

const RecruitmentPage = () => {
  const [tab, setTab] = useState('jobs');
  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      <div className="mb-4 sm:mb-6">
        <span className="text-xs font-semibold tracking-[0.25em] text-[#0f5c3d] uppercase">Recruitment · ATS</span>
        <h1 className="mt-2 text-3xl sm:text-4xl text-[#0b1a2e]" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 500 }}>Jobs & candidates pipeline</h1>
        <p className="mt-1 text-xs sm:text-sm text-[#0b1a2e]/60">Track candidates from application to onboarding. Auto-generate appointment & rejection letters.</p>
      </div>
      <div className="flex border-b border-black/5 bg-[#f7fbf8] rounded-t-2xl overflow-x-auto mb-4">
        {[['jobs', 'Job requisitions'], ['pipeline', 'Candidate pipeline']].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`rec-tab-${k}`}
            className={`px-4 py-2.5 text-sm font-medium ${tab === k ? 'text-[#0f5c3d] bg-white border-b-2 border-[#0f5c3d]' : 'text-[#0b1a2e]/60'}`}>{l}</button>
        ))}
      </div>
      {tab === 'jobs' && <JobsTab />}
      {tab === 'pipeline' && <PipelineTab />}
    </div>
  );
};

const JobsTab = () => {
  const [items, setItems] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const load = useCallback(async () => { const r = await api.jobs(); setItems(r.data || []); }, []);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="bg-white border border-black/5 rounded-2xl overflow-hidden">
      <div className="p-3 border-b border-black/5 flex justify-between">
        <div className="text-sm">{items.length} jobs</div>
        <button onClick={() => setShowNew(true)} data-testid="new-job-btn" className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#0f5c3d] text-white text-xs"><Plus className="w-3.5 h-3.5" /> New</button>
      </div>
      <table className="w-full text-sm">
        <thead className="bg-[#f7fbf8]"><tr>
          <th className="text-left p-2 text-xs uppercase text-[#0b1a2e]/60">Title</th>
          <th className="text-left p-2 text-xs uppercase text-[#0b1a2e]/60">Country</th>
          <th className="text-right p-2 text-xs uppercase text-[#0b1a2e]/60">Count</th>
          <th className="text-right p-2 text-xs uppercase text-[#0b1a2e]/60">Candidates</th>
          <th className="text-right p-2 text-xs uppercase text-[#0b1a2e]/60">Salary</th>
          <th className="text-left p-2 text-xs uppercase text-[#0b1a2e]/60">Status</th>
        </tr></thead>
        <tbody>{items.map((j) => (
          <tr key={j.id} className="border-t border-black/5" data-testid={`job-row-${j.id}`}>
            <td className="p-2 font-medium">{j.title}</td>
            <td className="p-2 text-xs">{j.country || '—'}</td>
            <td className="p-2 text-right">{j.count}</td>
            <td className="p-2 text-right">{j.candidate_count || 0}</td>
            <td className="p-2 text-right text-xs">{j.min_salary ? `${j.currency || ''} ${j.min_salary}-${j.max_salary}` : '—'}</td>
            <td className="p-2"><span className={`text-[10px] uppercase font-bold px-1.5 py-0.5 rounded ${j.status === 'open' ? 'bg-emerald-100 text-emerald-800' : j.status === 'on_hold' ? 'bg-amber-100 text-amber-800' : 'bg-slate-200'}`}>{j.status}</span></td>
          </tr>
        ))}</tbody>
      </table>
      {items.length === 0 && <div className="p-8 text-center text-sm text-[#0b1a2e]/50">No jobs yet.</div>}
      {showNew && <NewJobModal onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); load(); }} />}
    </div>
  );
};

const NewJobModal = ({ onClose, onSaved }) => {
  const [f, setF] = useState({ title: '', country: 'IN', count: 1, description: '', min_salary: '', max_salary: '', currency: 'INR', status: 'open' });
  const save = async () => {
    try {
      await api.createJob({ ...f, count: parseInt(f.count, 10), min_salary: f.min_salary ? parseFloat(f.min_salary) : null, max_salary: f.max_salary ? parseFloat(f.max_salary) : null });
      toast({ title: 'Job created' }); onSaved();
    } catch (e) { toast({ title: e?.response?.data?.detail || 'Failed', variant: 'destructive' }); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg overflow-hidden">
        <div className="px-5 py-4 bg-[#0b1a2e] text-white flex justify-between"><div className="font-medium">New job requisition</div><button onClick={onClose}><X className="w-5 h-5" /></button></div>
        <div className="p-5 space-y-3">
          <div><label className="text-[10px] uppercase font-semibold text-[#0b1a2e]/60">Title</label>
            <input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="job-title" className="w-full px-3 py-2 border rounded text-sm" /></div>
          <div className="grid grid-cols-2 gap-2">
            <div><label className="text-[10px] uppercase font-semibold text-[#0b1a2e]/60">Country</label>
              <select value={f.country} onChange={(e) => setF({ ...f, country: e.target.value })} className="w-full px-3 py-2 border rounded text-sm bg-white">
                <option>IN</option><option>UK</option><option>MY</option><option>GENERIC</option>
              </select></div>
            <div><label className="text-[10px] uppercase font-semibold text-[#0b1a2e]/60">Count</label>
              <input type="number" value={f.count} onChange={(e) => setF({ ...f, count: e.target.value })} className="w-full px-3 py-2 border rounded text-sm" /></div>
            <div><label className="text-[10px] uppercase font-semibold text-[#0b1a2e]/60">Min salary</label>
              <input type="number" value={f.min_salary} onChange={(e) => setF({ ...f, min_salary: e.target.value })} className="w-full px-3 py-2 border rounded text-sm" /></div>
            <div><label className="text-[10px] uppercase font-semibold text-[#0b1a2e]/60">Max salary</label>
              <input type="number" value={f.max_salary} onChange={(e) => setF({ ...f, max_salary: e.target.value })} className="w-full px-3 py-2 border rounded text-sm" /></div>
          </div>
          <div><label className="text-[10px] uppercase font-semibold text-[#0b1a2e]/60">Description</label>
            <textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} rows={4} className="w-full px-3 py-2 border rounded text-sm" /></div>
          <button onClick={save} data-testid="job-save-btn" className="w-full mt-2 px-4 py-2 rounded-full bg-[#0f5c3d] text-white text-sm font-semibold">Create</button>
        </div>
      </div>
    </div>
  );
};

const PipelineTab = () => {
  const [candidates, setCandidates] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [jobFilter, setJobFilter] = useState('');
  const [sortByScore, setSortByScore] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [onboardCand, setOnboardCand] = useState(null);
  const load = useCallback(async () => {
    const [c, j] = await Promise.all([api.candidates(jobFilter || null, sortByScore ? 'score' : null), api.jobs()]);
    setCandidates(c.data || []); setJobs(j.data || []);
  }, [jobFilter, sortByScore]);
  useEffect(() => { load(); }, [load]);
  const jobById = Object.fromEntries(jobs.map((j) => [j.id, j]));
  const moveStage = async (c, stage) => {
    const extra = {};
    if (stage === 'offered') {
      const s = window.prompt('Offered salary (annual)?'); const ccy = window.prompt('Currency? (INR/GBP/MYR)', 'INR');
      const dt = window.prompt('Joining date? YYYY-MM-DD');
      if (s) { extra.offered_salary = parseFloat(s); extra.offered_currency = ccy || 'INR'; if (dt) extra.joining_date = dt; }
    }
    try {
      await api.moveStage(c.id, { stage, ...extra });
      toast({ title: `Moved to ${stage}` });
      load();
    } catch (e) { toast({ title: e?.response?.data?.detail || 'Failed', variant: 'destructive' }); }
  };
  const rescoreAll = async () => {
    try { const r = await api.rescoreAll(jobFilter || null); toast({ title: `Rescored ${r.rescored} candidates` }); load(); }
    catch (e) { toast({ title: e?.response?.data?.detail || 'Failed', variant: 'destructive' }); }
  };
  const groups = STAGES.map((s) => ({ ...s, items: candidates.filter((c) => c.stage === s.v) }));
  return (
    <div>
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <select value={jobFilter} onChange={(e) => setJobFilter(e.target.value)} data-testid="rec-job-filter" className="px-3 py-2 border rounded text-sm bg-white">
          <option value="">All jobs</option>
          {jobs.map((j) => <option key={j.id} value={j.id}>{j.title}</option>)}
        </select>
        <label className="inline-flex items-center gap-1.5 text-xs">
          <input type="checkbox" checked={sortByScore} onChange={(e) => setSortByScore(e.target.checked)} data-testid="sort-by-score" /> Sort by match score
        </label>
        <button onClick={rescoreAll} data-testid="rescore-all-btn" className="text-xs px-3 py-2 rounded-full border border-[#0f5c3d]/20 hover:bg-[#f7fbf8]">Rescore all</button>
        <button onClick={() => setShowNew(true)} data-testid="new-candidate-btn" className="ml-auto inline-flex items-center gap-1 px-3 py-2 rounded-full bg-[#0f5c3d] text-white text-xs"><Plus className="w-3.5 h-3.5" /> New candidate</button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-6 gap-2 overflow-x-auto">
        {groups.map((g) => (
          <div key={g.v} className="bg-white border border-black/5 rounded-2xl overflow-hidden min-w-[220px]">
            <div className={`px-3 py-2 text-xs font-bold uppercase tracking-widest ${g.color}`}>{g.l} ({g.items.length})</div>
            <div className="p-2 space-y-2 max-h-[70vh] overflow-y-auto">
              {g.items.map((c) => (
                <div key={c.id} className="bg-[#f7fbf8] p-2 rounded" data-testid={`candidate-${c.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium truncate">{c.name}</div>
                      <div className="text-[10px] text-[#0b1a2e]/60 truncate">{jobById[c.req_id]?.title || 'Job removed'}</div>
                    </div>
                    {typeof c.match_score === 'number' && (
                      <div className={`shrink-0 text-[10px] font-bold px-1.5 py-0.5 rounded ${c.match_score >= 70 ? 'bg-emerald-100 text-emerald-800' : c.match_score >= 40 ? 'bg-amber-100 text-amber-800' : 'bg-slate-200 text-slate-700'}`} data-testid={`score-${c.id}`}>{c.match_score}%</div>
                    )}
                  </div>
                  {c.email && <div className="text-[10px] text-[#0b1a2e]/50 truncate">{c.email}</div>}
                  {c.offered_salary && <div className="text-[10px] mt-1 text-emerald-700">{c.offered_currency} {c.offered_salary?.toLocaleString?.() || c.offered_salary}</div>}
                  <div className="mt-2 flex items-center gap-1 flex-wrap">
                    {STAGES.filter((s) => s.v !== c.stage).slice(0, 5).map((s) => (
                      <button key={s.v} onClick={() => moveStage(c, s.v)} data-testid={`move-${c.id}-${s.v}`}
                        className="text-[9px] px-1 py-0.5 rounded border border-[#0f5c3d]/20 hover:bg-white">→ {s.l}</button>
                    ))}
                  </div>
                  {c.stage === 'offered' && <button onClick={() => downloadLetter(c.id, 'appointment', c.name)} data-testid={`letter-appointment-${c.id}`}
                    className="mt-1 w-full inline-flex items-center gap-1 justify-center text-[10px] px-2 py-1 rounded bg-emerald-600 text-white"><FileDown className="w-3 h-3" /> Appointment PDF</button>}
                  {c.stage === 'hired' && !c.onboarded_user_id && <button onClick={() => setOnboardCand(c)} data-testid={`onboard-btn-${c.id}`}
                    className="mt-1 w-full inline-flex items-center gap-1 justify-center text-[10px] px-2 py-1 rounded bg-[#0f5c3d] text-white hover:bg-[#0b4a30]"><UserCheck className="w-3 h-3" /> Send for onboarding</button>}
                  {c.stage === 'hired' && c.onboarded_user_id && <div className="mt-1 text-[10px] text-center text-emerald-700 bg-emerald-50 rounded px-2 py-1">✓ Onboarded</div>}
                  {c.stage === 'rejected' && <button onClick={() => downloadLetter(c.id, 'rejection', c.name)} data-testid={`letter-rejection-${c.id}`}
                    className="mt-1 w-full inline-flex items-center gap-1 justify-center text-[10px] px-2 py-1 rounded bg-red-600 text-white"><FileDown className="w-3 h-3" /> Rejection PDF</button>}
                </div>
              ))}
              {g.items.length === 0 && <div className="text-center text-[10px] text-[#0b1a2e]/40 py-4">No candidates</div>}
            </div>
          </div>
        ))}
      </div>
      {showNew && <NewCandidateModal jobs={jobs} onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); load(); }} />}
      {onboardCand && <OnboardModal candidate={onboardCand} job={jobById[onboardCand.req_id]} onClose={() => setOnboardCand(null)} onDone={() => { setOnboardCand(null); load(); }} />}
    </div>
  );
};

const OnboardModal = ({ candidate, job, onClose, onDone }) => {
  const emailGuess = candidate.email || '';
  const [f, setF] = useState({
    name: candidate.name || '',
    email: emailGuess,
    role: 'staff',
    country: 'IN',
    hire_date: candidate.joining_date || new Date().toISOString().slice(0, 10),
    employee_code: '',
    designation: job?.title || '',
    contract_type: 'permanent',
    send_invite: true,
    offered_salary: candidate.offered_salary || null,
    offered_currency: candidate.offered_currency || null,
    submitter_note: '',
  });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!f.name || !f.email) { toast({ title: 'Name and email are required', variant: 'destructive' }); return; }
    setBusy(true);
    try {
      await apiClient.post(`/hr/onboarding/from-candidate/${candidate.id}`, f);
      toast({ title: 'Sent to HR review', description: 'Both HR and Admin must approve before the user is created.' });
      onDone();
    } catch (e) { toast({ title: e?.response?.data?.detail || 'Failed', variant: 'destructive' }); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="onboard-submit-modal">
      <div className="bg-white rounded-2xl w-full max-w-xl overflow-hidden">
        <div className="px-5 py-4 bg-[#0b1a2e] text-white flex justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#7bbf95]">Send for onboarding</div>
            <div className="font-medium">{candidate.name}</div>
          </div>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-3">
          <div className="text-xs text-[#0b1a2e]/60 bg-[#f7fbf8] p-2 rounded">Two-step approval: HR reviews first, then Admin signs off and the user account is created automatically.</div>
          <div className="grid grid-cols-2 gap-3">
            <input placeholder="Name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="px-3 py-2 border rounded text-sm" />
            <input placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="onboard-submit-email" className="px-3 py-2 border rounded text-sm" />
            <select value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} className="px-3 py-2 border rounded text-sm bg-white">
              {['staff', 'hr', 'accounts', 'content_publisher', 'admin'].map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
            <select value={f.country} onChange={(e) => setF({ ...f, country: e.target.value })} className="px-3 py-2 border rounded text-sm bg-white">
              {['IN', 'UK', 'MY', 'GENERIC'].map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <input type="date" value={f.hire_date} onChange={(e) => setF({ ...f, hire_date: e.target.value })} className="px-3 py-2 border rounded text-sm" />
            <input placeholder="Employee code (optional)" value={f.employee_code} onChange={(e) => setF({ ...f, employee_code: e.target.value })} className="px-3 py-2 border rounded text-sm" />
            <input placeholder="Designation" value={f.designation} onChange={(e) => setF({ ...f, designation: e.target.value })} className="px-3 py-2 border rounded text-sm col-span-2" />
          </div>
          <textarea placeholder="Note for HR reviewer (optional)" value={f.submitter_note} onChange={(e) => setF({ ...f, submitter_note: e.target.value })} rows={2} className="w-full px-3 py-2 border rounded text-sm" />
          <button onClick={submit} disabled={busy} data-testid="onboard-submit-btn" className="w-full px-4 py-2 rounded-full bg-[#0f5c3d] text-white text-sm font-semibold disabled:opacity-50">
            {busy && <Loader2 className="w-4 h-4 inline animate-spin mr-1" />} Send to HR queue
          </button>
        </div>
      </div>
    </div>
  );
};

const NewCandidateModal = ({ jobs, onClose, onSaved }) => {
  const [f, setF] = useState({ name: '', email: '', phone: '', req_id: '', resume_url: '', resume_text: '', notes: '', rating: null, stage: 'applied' });
  const save = async () => {
    try {
      const p = { ...f };
      if (!p.email) delete p.email;
      if (!p.rating) delete p.rating;
      await api.createCandidate(p);
      toast({ title: 'Candidate added' });
      onSaved();
    } catch (e) { toast({ title: e?.response?.data?.detail || 'Failed', variant: 'destructive' }); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden">
        <div className="px-5 py-4 bg-[#0b1a2e] text-white flex justify-between"><div className="font-medium">New candidate</div><button onClick={onClose}><X className="w-5 h-5" /></button></div>
        <div className="p-5 space-y-3">
          <input placeholder="Name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="cand-name" className="w-full px-3 py-2 border rounded text-sm" />
          <input placeholder="Email (optional)" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="w-full px-3 py-2 border rounded text-sm" />
          <input placeholder="Phone" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} className="w-full px-3 py-2 border rounded text-sm" />
          <select value={f.req_id} onChange={(e) => setF({ ...f, req_id: e.target.value })} data-testid="cand-job" className="w-full px-3 py-2 border rounded text-sm bg-white">
            <option value="">Select job</option>
            {jobs.filter((j) => j.status === 'open').map((j) => <option key={j.id} value={j.id}>{j.title}</option>)}
          </select>
          <input placeholder="Resume URL (optional)" value={f.resume_url} onChange={(e) => setF({ ...f, resume_url: e.target.value })} className="w-full px-3 py-2 border rounded text-sm" />
          <textarea placeholder="Resume text (paste for match scoring)" value={f.resume_text} onChange={(e) => setF({ ...f, resume_text: e.target.value })} rows={4} className="w-full px-3 py-2 border rounded text-sm" />
          <textarea placeholder="Notes" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} rows={3} className="w-full px-3 py-2 border rounded text-sm" />
          <button onClick={save} data-testid="cand-save-btn" className="w-full px-4 py-2 rounded-full bg-[#0f5c3d] text-white text-sm">Add candidate</button>
        </div>
      </div>
    </div>
  );
};

export default RecruitmentPage;
